// Service Request list page — server-driven via DataTable.

import { useCallback, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SERVICE_TYPES, SERVICE_TYPE_LABELS } from "@hcm/core";
import { apiFetch } from "../lib/api.js";
import { formatSrRef, verbalizeSrRef } from "../lib/refs.js";
import { useAuth } from "../hooks/useAuth.js";
import { Shell } from "../components/Shell.js";
import { SrStatusBadge } from "../components/SrStatusBadge.js";
import { RiskBadge } from "../components/RiskBadge.js";
import { NewSrModal } from "../components/NewSrModal.js";
import { downloadCsv } from "../lib/csv-export.js";
import { useSrBulkActions } from "../hooks/useSrBulkActions.js";
import { useHighlightedRow } from "../hooks/useHighlightedRow.js";
import {
  DataTable,
  useDataTable,
  type ColumnDef,
  type ListQuery,
} from "../components/DataTable/index.js";

const COORDINATOR_ROLES = new Set(["coordinator", "agency_admin"]);

async function bulkPatch<T>(ids: readonly string[], path: (id: string) => string, body?: T): Promise<{ ok: number; failed: number }> {
  const opts: Parameters<typeof apiFetch>[1] = body
    ? { method: "PATCH", body: JSON.stringify(body) }
    : { method: "PATCH" };
  const results = await Promise.allSettled(ids.map((id) => apiFetch(path(id), opts)));
  const ok = results.filter((r) => r.status === "fulfilled").length;
  return { ok, failed: results.length - ok };
}

function reportBulkResult(verb: string, ok: number, failed: number): void {
  if (failed === 0) toast.success(`${verb} ${ok}.`);
  else toast.warning(`${verb} ${ok}. ${failed} failed.`);
}

interface Sr {
  _id: string;
  status: string;
  service_type: string;
  scheduled_datetime?: string;
  risk_score_band?: string | null;
  patient_id: string;
  nsofar_sr_id?: string | null;
}

interface SrListResponse { data: Sr[]; total: number; page: number; limit: number; }

const STATUS_TABS = [
  { label: "Active",    value: "new,assigned,scheduled,in_progress" },
  { label: "Completed", value: "completed,confirmed" },
  { label: "All",       value: "" },
];

// Q7 S4 — sourced from @hcm/core; previously a local 6-value duplicate
// that omitted medical_nursing, nutritionist_call, diagnostics_visit, and
// property_visit. Backend filter uses an exact match on service_type, so
// this MUST stay aligned with the canonical 10 (TASK-43 closure).
const SERVICE_TYPE_OPTIONS = SERVICE_TYPES.map((value) => ({
  value,
  label: SERVICE_TYPE_LABELS[value],
}));

function buildQs(query: ListQuery, statusFilter: string): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    p.set(k, String(v));
  }
  if (statusFilter) p.set("status", statusFilter);
  return p.toString();
}

function useSrList(query: ListQuery, statusFilter: string): ReturnType<typeof useQuery<SrListResponse>> {
  const qs = buildQs(query, statusFilter);
  return useQuery<SrListResponse>({
    queryKey: ["sr-list", qs],
    queryFn: () => apiFetch<SrListResponse>(`/sr?${qs}`),
    refetchInterval: 30_000,
  });
}

function StatusTabs({ active, onChange }: { active: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
      {STATUS_TABS.map((t) => (
        <button
          key={t.label}
          onClick={() => onChange(t.value)}
          className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${active === t.value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
}

function buildColumns(navigate: ReturnType<typeof useNavigate>): ReadonlyArray<ColumnDef<Sr>> {
  return [
    { accessorKey: "_id", header: "Ref #",
      cell: (sr) => <span className="font-mono text-xs text-slate-500">{formatSrRef(sr)}</span> },
    { accessorKey: "status", header: "Status", sortable: true,
      cell: (sr) => <SrStatusBadge status={sr.status} /> },
    { accessorKey: "service_type", header: "Type",
      filterable: true, filterType: "select", filterOptions: SERVICE_TYPE_OPTIONS,
      cell: (sr) => <span className="text-slate-700">{sr.service_type.replace(/_/g, " ")}</span> },
    { accessorKey: "scheduled_datetime", header: "Scheduled", sortable: true,
      cell: (sr) => <span className="text-slate-500">{fmtDate(sr.scheduled_datetime)}</span> },
    { accessorKey: "risk_score_band", header: "Risk",
      cell: (sr) => <RiskBadge band={sr.risk_score_band ?? null} /> },
    { accessorKey: "actions", header: "Actions", align: "right",
      cell: (sr) => (
        <button
          type="button"
          onClick={() => navigate(`/sr/${sr._id}`)}
          aria-label={`View service request ${verbalizeSrRef(sr)}`}
          className="text-sm text-navy-800 hover:text-navy-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          View
        </button>
      ),
    },
  ];
}

interface SrBulkBarProps {
  ids: readonly string[];
  rows: readonly Sr[];
  canCoordinate: boolean;
  onAskCancel: () => void;
  onAskReassign: () => void;
}

function SrBulkBar({ ids, rows, canCoordinate, onAskCancel, onAskReassign }: SrBulkBarProps): ReactNode {
  function exportCsv(): void {
    const selected = rows.filter((r) => ids.includes(r._id));
    downloadCsv(`service-requests-${new Date().toISOString().slice(0, 10)}.csv`, [
      { header: "Ref",       value: (r) => formatSrRef(r) },
      { header: "Status",    value: (r) => r.status },
      { header: "Type",      value: (r) => r.service_type },
      { header: "Scheduled", value: (r) => r.scheduled_datetime ?? "" },
      { header: "Risk",      value: (r) => r.risk_score_band ?? "" },
      { header: "Patient",   value: (r) => r.patient_id },
      { header: "Nsofar SR", value: (r) => r.nsofar_sr_id ?? "" },
    ], selected);
    toast.success(`Exported ${selected.length} service request${selected.length === 1 ? "" : "s"}.`);
  }
  return (
    <>
      {canCoordinate && (
        <button type="button" onClick={onAskCancel}
          className="text-xs text-red-700 hover:text-red-900 underline">Cancel selected</button>
      )}
      {canCoordinate && (
        <button type="button" onClick={onAskReassign}
          className="text-xs text-navy-800 hover:text-navy-950 underline">Reassign carer</button>
      )}
      <button type="button" onClick={exportCsv}
        className="text-xs text-navy-800 hover:text-navy-950 underline">Export CSV</button>
    </>
  );
}

// eslint-disable-next-line complexity -- top-level page hook with 11 reactive pieces (state slots + ??-narrowed defaults). Per-piece extraction would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function SrListPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCoordinate = COORDINATOR_ROLES.has(user?.role ?? "");
  const [statusFilter, setStatusFilter] = useState(STATUS_TABS[0]?.value ?? "");
  const [showNew, setShowNew] = useState(false);
  const { highlightId } = useHighlightedRow();
  const tableState = useDataTable({
    pageSize: 20,
    defaultSort: { sortBy: "scheduled_datetime", order: "desc" },
  });
  const columns = useMemo(() => buildColumns(navigate), [navigate]);
  const { data, isLoading, isError, refetch } = useSrList(tableState.query, statusFilter);
  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const { api: bulkApi, modals: bulkModals } = useSrBulkActions<Sr>({
    rows, refetch, clearSelection: tableState.selection.clear,
    refLabel: (r) => formatSrRef(r),
  });

  const renderBulkActions = useCallback((ids: readonly string[]): ReactNode => (
    <SrBulkBar
      ids={ids} rows={rows} canCoordinate={canCoordinate}
      onAskCancel={() => bulkApi.askCancel(ids)}
      onAskReassign={() => bulkApi.askReassign(ids)}
    />
  ), [rows, canCoordinate, bulkApi]);

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-slate-900">
            Service Requests <span className="text-sm font-normal text-slate-400">({total})</span>
          </h1>
          <button
            onClick={() => setShowNew(true)}
            className="bg-navy-700 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy-800 transition-colors"
          >
            + New SR
          </button>
        </div>
        <StatusTabs
          active={statusFilter}
          onChange={(v) => { setStatusFilter(v); tableState.setPage(1); }}
        />
        <DataTable<Sr>
          state={tableState}
          columns={columns}
          data={rows}
          total={total}
          rowKey={(sr) => sr._id}
          ariaLabel="Service requests"
          isLoading={isLoading}
          isError={isError}
          errorMessage="Failed to load service requests."
          emptyState="No service requests found."
          searchPlaceholder="Search by ref"
          enableDateRange
          selectable
          bulkActions={renderBulkActions}
          highlightId={highlightId ?? undefined}
        />
      </div>
      {showNew && (
        <NewSrModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); void refetch(); }}
        />
      )}
      {bulkModals}
    </Shell>
  );
}

export { COORDINATOR_ROLES, bulkPatch, reportBulkResult };
